/**
 * Which persona the signed-in person is currently using.
 *
 * The API deliberately does not put a role on the user object: authorisation is
 * loaded per request from the database, because a role baked into a 15-minute
 * token stays in force for 15 minutes after it is revoked. So the client cannot
 * read `user.role`: there is no such field, and the previous code's
 * `profile.role !== "Driver"` checks were reading `undefined` on every render
 * and taking the rider branch unconditionally.
 *
 * What the client can know is whether this person has an approved driver
 * application, which is exactly the question the driver screens need answered.
 * A 404 from that endpoint means "has never applied", which is a fact, not an
 * error, and is reported as `hasApplied: false`.
 */

import { useLocation } from "react-router-dom";

import { useDriverApplicationQuery } from "../api";
import type { DriverApprovalStatus } from "../api/types";
import { useIsAuthenticated } from "../store/hooks";

export type Persona = "rider" | "driver";

export interface PersonaState {
    /** The dashboard the person is looking at right now, from the URL. */
    persona: Persona;
    /** Whether they hold an approved driver application. */
    isApprovedDriver: boolean;
    /** Whether they have applied at all, whatever the outcome. */
    hasApplied: boolean;
    applicationStatus: DriverApprovalStatus | null;
    isLoading: boolean;
}

export function usePersona(): PersonaState {
    const location = useLocation();
    const persona: Persona = location.pathname.includes("/driver")
        ? "driver"
        : "rider";

    // Skipped without a session, because /driver/application requires one.
    //
    // Navbar calls this hook, and Navbar is on every public page, so an
    // unconditional query meant a signed-out visitor spent a request and
    // collected a 401 in the console on the landing page, ride, drive, safety,
    // company and the legal pages. It never produced a wrong answer, because a
    // 401 is not a 404 and so left `hasApplied` true rather than false, but it
    // was one wasted round trip per page view for everyone who has not signed
    // up yet, which is most visitors.
    const signedIn = useIsAuthenticated();
    const { data, error, isLoading } = useDriverApplicationQuery(undefined, {
        skip: !signedIn,
    });

    // A 404 is the documented answer for "no application exists". Anything else
    // is a real failure, and in that case we say nothing rather than claiming
    // the person has not applied.
    const notFound =
        error !== undefined &&
        typeof error === "object" &&
        "status" in error &&
        (error as { status?: unknown }).status === 404;

    return {
        persona,
        isApprovedDriver: data?.status === "approved",
        hasApplied: Boolean(data) || (error !== undefined && !notFound),
        applicationStatus: data?.status ?? null,
        isLoading,
    };
}

export default usePersona;
